import { useEffect, useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { getClientHistory, getClients, getDashboardOverview } from "../lib/api";
import type { Client, ClientHistoryResponse, DashboardOverview, WeeklyCheckIn } from "../types";

type TopWeightPoint = { client: string; currentWeight: number; objective: string; checkInDate: string };
type DonutMetric = "status" | "plan";

const FALLBACK_CLIENTS: DashboardOverview["clients"] = [
  { id: "demo-1", fullName: "Maria Gonzalez", objective: "Pérdida de grasa", status: "PROGRESS", latestCheckInDate: "2026-05-25" },
  { id: "demo-2", fullName: "Carlos Ramirez", objective: "Ganar masa muscular", status: "PROGRESS", latestCheckInDate: "2026-05-25" },
  { id: "demo-3", fullName: "Ana Torres", objective: "Tonificación", status: "PROGRESS", latestCheckInDate: "2026-05-24" },
  { id: "demo-4", fullName: "Luis Fernandez", objective: "Mejorar resistencia", status: "STAGNATED", latestCheckInDate: "2026-05-23" },
  { id: "demo-5", fullName: "Diego Morales", objective: "Pérdida de grasa", status: "NEW", latestCheckInDate: "2026-05-21" },
];

const DONUT_GRADIENT_STOPS: Array<[string, string]> = [
  ["#ff7a3b", "#e23e00"],
  ["#ffb547", "#f08000"],
  ["#3ed1bd", "#0d8a76"],
];
const STALE_CHECKIN_DAYS = 7;
const showLegacyDashboardSections = false;
const PLAN_OPTIONS = ["Plan Básico", "Plan Elite", "Plan Performance"];
const DONUT_METRICS: Array<{ key: DonutMetric; label: string }> = [
  { key: "status", label: "Estado" },
  { key: "plan", label: "Plan" },
];

type TopWeightTooltipEntry = { value: number | string; payload: TopWeightPoint };
type TopWeightTooltipPayload = ReadonlyArray<TopWeightTooltipEntry>;

function TopWeightTooltip({ active, payload }: { active?: boolean; payload?: TopWeightTooltipPayload }) {
  if (!active || !payload?.length) return null;
  const point = payload[0]?.payload;
  if (!point) return null;
  return (
    <div className="db2-chart-tip">
      <span className="db2-chart-tip-label">{point.client}</span>
      <strong className="db2-chart-tip-value">
        {point.currentWeight.toFixed(1)}
        <span>kg actuales</span>
      </strong>
      <div className="db2-chart-tip-list">
        <span className="db2-chart-tip-row">
          <i style={{ background: "linear-gradient(135deg, #3ed1bd, #0d8a76)" }} />
          Objetivo
          <b>{point.objective || "Pendiente"}</b>
        </span>
        <span className="db2-chart-tip-row">
          <i style={{ background: "linear-gradient(135deg, #ffb547, #f08000)" }} />
          Check-in
          <b>{lastCheckin(point.checkInDate)}</b>
        </span>
      </div>
    </div>
  );
}

function lastCheckin(latestCheckInDate: string | null): string {
  if (!latestCheckInDate) return "-";

  try {
    return new Date(latestCheckInDate).toLocaleDateString("es-PE", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "-";
  }
}

function parseDate(value: string | null): Date | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function daysSince(value: string | null, now = new Date()): number | null {
  const date = parseDate(value);
  if (!date) return null;
  return Math.max(0, Math.floor((now.getTime() - date.getTime()) / 86_400_000));
}

function newestCheckIn(checkIns: WeeklyCheckIn[]): WeeklyCheckIn | null {
  return [...checkIns].sort((a, b) => new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime())[0] ?? null;
}

function weightFromLatestCheckIn(history: ClientHistoryResponse): TopWeightPoint | null {
  const checkIn = newestCheckIn(history.checkIns);
  if (!checkIn?.dailyWeights.length) return null;

  const latestWeight = [...checkIn.dailyWeights].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0];
  if (!latestWeight || !Number.isFinite(latestWeight.weightKg) || latestWeight.weightKg <= 0) return null;

  return {
    client: `${history.client.firstName} ${history.client.lastName}`.trim(),
    currentWeight: latestWeight.weightKg,
    objective: history.client.objective,
    checkInDate: checkIn.submittedAt,
  };
}

function percent(value: number, total: number) {
  if (!total) return "0%";
  return `${Math.round((value / total) * 100)}%`;
}

function initials(name: string) {
  return name
    .split(" ")
    .map((word) => word[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

function formatStatus(status: string) {
  const map: Record<string, string> = {
    PROGRESS: "Activo",
    STAGNATED: "Pendiente",
    REGRESSION: "Pendiente",
    NEW: "Nuevo",
  };
  return map[status] ?? status;
}

function normalizePlanName(planName: string | undefined): string {
  const normalized = (planName || "").trim().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

  if (normalized === "plan basico" || normalized === "basico" || normalized === "basic") return "Plan Básico";
  if (normalized === "plan elite" || normalized === "elite") return "Plan Elite";
  if (normalized === "plan performance" || normalized === "performance") return "Plan Performance";

  return "";
}

function Spark() {
  return (
    <svg viewBox="0 0 120 40" className="db2-kpi-spark" preserveAspectRatio="none" aria-hidden="true">
      <path
        d="M0,32 C8,28 14,35 25,26 C36,17 42,22 54,18 C66,14 72,20 84,12 C96,4 104,10 120,5"
        fill="none"
        stroke="rgba(255,255,255,0.72)"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function DashboardPage() {
  const [data, setData] = useState<DashboardOverview | null>(null);
  const [checkInWeights, setCheckInWeights] = useState<TopWeightPoint[]>([]);
  const [dashboardClients, setDashboardClients] = useState<Client[]>([]);
  const [donutMetric, setDonutMetric] = useState<DonutMetric>("status");

  useEffect(() => {
    let cancelled = false;

    getDashboardOverview().then(setData).catch(() => setData(null));

    getClients()
      .then((loadedClients) => {
        if (!cancelled) setDashboardClients(loadedClients);
        return Promise.all(loadedClients.map((client) => getClientHistory(client.id).catch(() => null)));
      })
      .then((histories) => {
        if (cancelled) return;
        setCheckInWeights(
          histories
            .filter((history): history is ClientHistoryResponse => history !== null)
            .map(weightFromLatestCheckIn)
            .filter((point): point is TopWeightPoint => point !== null),
        );
      })
      .catch(() => {
        if (!cancelled) {
          setDashboardClients([]);
          setCheckInWeights([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const totalReal =
    (data?.metrics.progressingClients ?? 0) +
    (data?.metrics.stagnatedClients ?? 0) +
    (data?.metrics.regressionClients ?? 0);
  const useMock = totalReal < 5;
  const clients = data?.clients?.length ? data.clients : FALLBACK_CLIENTS;
  const totalClients = useMock ? clients.length : data?.metrics.totalClients ?? clients.length;
  const recentCheckIns = clients.filter((client) => {
    const age = daysSince(client.latestCheckInDate);
    return age !== null && age <= STALE_CHECKIN_DAYS;
  }).length;
  const staleCheckIns = clients.filter((client) => {
    const age = daysSince(client.latestCheckInDate);
    return age === null || age > STALE_CHECKIN_DAYS;
  }).length;
  const checkInsPending = staleCheckIns;
  const weeklyAdherence = percent(recentCheckIns, totalClients);
  const attentionClients = useMock
    ? clients.filter((client) => client.status === "STAGNATED" || client.status === "REGRESSION").length
    : (data?.metrics.stagnatedClients ?? 0) + (data?.metrics.regressionClients ?? 0);
  const riskClients = clients.filter((client) => {
    const age = daysSince(client.latestCheckInDate);
    return client.status === "STAGNATED" || client.status === "REGRESSION" || age === null || age > STALE_CHECKIN_DAYS;
  }).length;
  const followUps: DashboardOverview["clients"] = [];
  const recentActivity: Array<{ color: string; text: string; time: string }> = [];
  const weightChartData = useMemo(
    () =>
      checkInWeights
        .slice()
        .sort((a, b) => b.currentWeight - a.currentWeight)
        .slice(0, 5),
    [checkInWeights],
  );

  const donutData = useMemo(() => {
    if (donutMetric === "plan") {
      return PLAN_OPTIONS.map((plan) => ({
        name: plan.replace("Plan ", ""),
        value: dashboardClients.filter((client) => normalizePlanName(client.planName) === plan).length,
      }));
    }

    return [
      { name: "Activos", value: useMock ? clients.filter((client) => client.status === "PROGRESS").length : data?.metrics.progressingClients ?? 0 },
      { name: "Nuevos", value: clients.filter((client) => client.status === "NEW").length },
      { name: "Pendientes", value: useMock ? clients.filter((client) => client.status === "STAGNATED" || client.status === "REGRESSION").length : (data?.metrics.stagnatedClients ?? 0) + (data?.metrics.regressionClients ?? 0) },
    ];
  }, [checkInsPending, clients, dashboardClients, data, donutMetric, recentCheckIns, useMock]);

  const donutMeta = {
    status: { title: "Clientes por estado", subtitle: "Distribución entre activos, nuevos y pendientes.", center: "Clientes" },
    plan: { title: "Clientes por plan", subtitle: "Distribución según el plan asignado.", center: "Clientes" },
  }[donutMetric];
  const donutTotal = donutData.reduce((sum, item) => sum + item.value, 0);
  const kpiCards = [
    { label: "Clientes activos", value: totalClients, delta: `${recentCheckIns} con check-in reciente`, tone: "orange", icon: "clients" },
    { label: "Check-ins pendientes", value: checkInsPending, delta: `>${STALE_CHECKIN_DAYS} dias sin seguimiento`, tone: "amber", icon: "calendar" },
    { label: "Adherencia semanal", value: weeklyAdherence, delta: `${recentCheckIns} de ${totalClients} con check-in`, tone: "teal", icon: "target" },
    { label: "Clientes en riesgo", value: riskClients, delta: `${attentionClients} por estado critico`, tone: "coral", icon: "alert" },
  ];

  return (
    <div className="db2-page">
      <div className="db2-header">
        <div>
          <h1 className="db2-title">Dashboard del Coach</h1>
          <p className="db2-subtitle">Resumen general del rendimiento de clientes y seguimiento semanal.</p>
        </div>
        <div className="db2-date-pill">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16" aria-hidden="true">
            <rect x="3" y="4" width="18" height="18" rx="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
          19 - 25 mayo 2026
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14" aria-hidden="true">
            <path d="m6 9 6 6 6-6" />
          </svg>
        </div>
      </div>

      <div className="db2-kpi-grid">
        {kpiCards.map((card) => (
          <article key={card.label} className={`db2-kpi db2-kpi--${card.tone}`}>
            <div className="db2-kpi-top">
              <span className="db2-kpi-icon" aria-hidden="true">
                {card.icon === "clients" ? (
                  <svg viewBox="0 0 24 24"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /></svg>
                ) : card.icon === "calendar" ? (
                  <svg viewBox="0 0 24 24"><rect x="4" y="4" width="16" height="18" rx="2" /><path d="M8 2v4M16 2v4M8 12h8M8 16h5" /></svg>
                ) : card.icon === "target" ? (
                  <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="8" /><circle cx="12" cy="12" r="3" /><path d="M12 2v4M22 12h-4" /></svg>
                ) : card.icon === "alert" ? (
                  <svg viewBox="0 0 24 24"><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" /><path d="M12 9v4M12 17h.01" /></svg>
                ) : (
                  <svg viewBox="0 0 24 24"><path d="M22 2 11 13" /><path d="m22 2-7 20-4-9-9-4 20-7Z" /></svg>
                )}
              </span>
              <div>
                <p className="db2-kpi-label">{card.label}</p>
                <p className="db2-kpi-value">{card.value}</p>
              </div>
            </div>
            <Spark />
            <p className="db2-kpi-delta">{card.delta}</p>
          </article>
        ))}
      </div>

      <div className="db2-charts-row">
        <section className="db2-panel db2-panel--wide db2-chart-panel">
          <div className="db2-panel-head">
            <div>
              <h3 className="db2-panel-title">Pesos actuales de clientes</h3>
              <p className="db2-panel-sub">Peso tomado del ultimo check-in registrado de cada cliente.</p>
            </div>
            <button className="db2-chip db2-chip--active" type="button">
              <span className="db2-chip-dot" /> {weightChartData.length} clientes
            </button>
          </div>
          {weightChartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart
              data={weightChartData}
              layout="vertical"
              margin={{ top: 12, right: 36, left: 16, bottom: 4 }}
              barCategoryGap="22%"
            >
              <defs>
                <linearGradient id="topWeightBar" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#ff9544" />
                  <stop offset="100%" stopColor="#ea580c" />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="2 6" horizontal={false} stroke="#eef0f4" />
              <XAxis
                type="number"
                tick={{ fontSize: 11, fill: "#9ca3af" }}
                axisLine={false}
                tickLine={false}
                domain={[0, "dataMax + 10"]}
                tickFormatter={(value) => `${value} kg`}
              />
              <YAxis
                type="category"
                dataKey="client"
                tick={{ fontSize: 12, fill: "#374151", fontWeight: 600 }}
                axisLine={false}
                tickLine={false}
                width={130}
              />
              <Tooltip
                content={<TopWeightTooltip />}
                cursor={{ fill: "rgba(255, 79, 15, 0.06)" }}
              />
              <Bar
                dataKey="currentWeight"
                fill="url(#topWeightBar)"
                radius={[0, 8, 8, 0]}
                maxBarSize={24}
                isAnimationActive
                animationDuration={900}
              >
                <LabelList
                  dataKey="currentWeight"
                  position="right"
                  formatter={(value) => `${Number(value).toFixed(1)} kg`}
                  style={{ fill: "#0f172a", fontSize: 12, fontWeight: 700 }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          ) : (
            <div className="history-chart-empty">Sin pesos registrados en el ultimo check-in.</div>
          )}
        </section>

        <section className="db2-panel db2-chart-panel">
          <div className="db2-panel-head">
            <div>
              <h3 className="db2-panel-title">{donutMeta.title}</h3>
              <p className="db2-panel-sub">{donutMeta.subtitle}</p>
            </div>
            <div className="db2-donut-modes" aria-label="Metrica del grafico">
              {DONUT_METRICS.map((metric) => (
                <button
                  key={metric.key}
                  type="button"
                  className={donutMetric === metric.key ? "is-active" : ""}
                  onClick={() => setDonutMetric(metric.key)}
                >
                  {metric.label}
                </button>
              ))}
            </div>
          </div>
          <div className="db2-donut-wrap">
            <div className="db2-donut-chart">
              <ResponsiveContainer width="100%" height={210}>
                <PieChart>
                  <defs>
                    {DONUT_GRADIENT_STOPS.map(([from, to], index) => (
                      <linearGradient key={`donut-grad-${index}`} id={`donutGrad${index}`} x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor={from} />
                        <stop offset="100%" stopColor={to} />
                      </linearGradient>
                    ))}
                    <filter id="donutShadow" x="-20%" y="-20%" width="140%" height="140%">
                      <feDropShadow dx="0" dy="6" stdDeviation="6" floodColor="#ff4f0f" floodOpacity="0.18" />
                    </filter>
                  </defs>
                  <Pie
                    data={donutData}
                    dataKey="value"
                    innerRadius="64%"
                    outerRadius="92%"
                    paddingAngle={donutData.filter((item) => item.value > 0).length > 1 ? 4 : 0}
                    cornerRadius={8}
                    startAngle={90}
                    endAngle={-270}
                    cx="50%"
                    cy="50%"
                    stroke="none"
                    filter="url(#donutShadow)"
                    isAnimationActive
                    animationDuration={900}
                  >
                    {donutData.map((item, index) => (
                      <Cell key={item.name} fill={`url(#donutGrad${index})`} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value, name) => {
                      const numeric = typeof value === "number" ? value : Number(value);
                      return [`${numeric} cliente${numeric === 1 ? "" : "s"}`, name];
                    }}
                    contentStyle={{
                      borderRadius: 12,
                      border: "none",
                      boxShadow: "0 12px 32px rgba(15,23,42,0.18)",
                      padding: "0.55rem 0.85rem",
                      fontSize: "0.8rem",
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="db2-donut-center">
                <strong>{donutTotal}</strong>
                <span>{donutMeta.center}</span>
              </div>
            </div>
            <div className="db2-legend">
              {donutData.map((item, index) => {
                const pct = donutTotal ? Math.round((item.value / donutTotal) * 100) : 0;
                return (
                  <div key={item.name} className="db2-legend-row">
                    <span
                      className="db2-legend-dot"
                      style={{ background: `linear-gradient(135deg, ${DONUT_GRADIENT_STOPS[index]?.[0]}, ${DONUT_GRADIENT_STOPS[index]?.[1]})` }}
                    />
                    <div className="db2-legend-meta">
                      <span className="db2-legend-name">{item.name}</span>
                      <div className="db2-legend-bar">
                        <span
                          className="db2-legend-bar-fill"
                          style={{
                            width: `${pct}%`,
                            background: `linear-gradient(90deg, ${DONUT_GRADIENT_STOPS[index]?.[0]}, ${DONUT_GRADIENT_STOPS[index]?.[1]})`,
                          }}
                        />
                      </div>
                    </div>
                    <span className="db2-legend-val">
                      {item.value}
                      <small>({pct}%)</small>
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      </div>

      {showLegacyDashboardSections && (
      <div className="db2-mid-row">
        <section className="db2-panel">
          <div className="db2-panel-head">
            <h3 className="db2-panel-title">Clientes por contactar</h3>
          </div>
          <div className="db2-checkin-list">
            {(followUps.length ? followUps : clients.slice(0, 4)).map((client) => {
              const age = daysSince(client.latestCheckInDate);
              return (
              <div key={client.id} className="db2-checkin-row">
                <div className="db2-ci-avatar">{initials(client.fullName)}</div>
                <div className="db2-ci-info">
                  <strong>{client.fullName}</strong>
                  <span>{client.objective || "Objetivo pendiente"}</span>
                </div>
                <div className="db2-ci-date">{lastCheckin(client.latestCheckInDate)}</div>
                <div className="db2-ci-time">{age === null ? "Sin registro" : `${age} días`}</div>
                <span className="db2-ci-status">{formatStatus(client.status)}</span>
              </div>
              );
            })}
          </div>
          <button className="db2-link-btn" type="button">Ver todos los check-ins →</button>
        </section>

        <section className="db2-panel">
          <div className="db2-panel-head">
            <h3 className="db2-panel-title">Actividad reciente</h3>
          </div>
          <div className="db2-activity-list">
            {recentActivity.map((activity) => (
              <div key={activity.text} className="db2-activity-row">
                <span className="db2-activity-dot" style={{ background: activity.color }} />
                <span className="db2-activity-text">{activity.text}</span>
                <span className="db2-activity-time">{activity.time}</span>
              </div>
            ))}
          </div>
          <button className="db2-link-btn" type="button">Ver toda la actividad →</button>
        </section>
      </div>
      )}

    </div>
  );
}
  
